import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  // 私有字段：当前加载指示器元素
  #loadingEl = null;
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  // 显示一个底部加载指示器
  #showLoading = () => {
    if (this.#loadingEl) return;

    // 将加载指示器放在 <dialog> 内部但位于 .cart-drawer__inner 之外，
    // 以避免在刷新 cart-drawer__inner 时被替换掉。
    const dialog = this.querySelector('.cart-drawer__dialog');
    if (!dialog) return;

    const loadingWrapper = document.createElement('div');
    loadingWrapper.className = 'cart-drawer__loading';
    // 绝对定位到底部居中，避免遮挡主要内容
    Object.assign(loadingWrapper.style, {
      position: 'absolute',
      left: '0',
      right: '0',
      bottom: '0',
      background: 'var(--color-background, #fff)',
    });
    loadingWrapper.innerHTML = '<div class="cart-drawer__spinner" aria-hidden="true"></div>';
    dialog.appendChild(loadingWrapper);
    this.#loadingEl = loadingWrapper;
  };

  // 移除加载指示器
  #hideLoading = () => {
    if (this.#loadingEl && this.#loadingEl.parentNode) {
      this.#loadingEl.parentNode.removeChild(this.#loadingEl);
    }
    this.#loadingEl = null;
  };

  #handleCartAdd = async () => {
    // 每次添加到购物车时立即打开抽屉并显示加载状态，再刷新 section
    const shouldOpen = this.hasAttribute('auto-open');
    if (shouldOpen) {
      this.showDialog();
    }
    this.#showLoading();

    try {
      await this.#refreshCartSections();
    } catch (e) {
      // 若刷新失败仅记录，不阻塞后续逻辑
      console.error('[cart-drawer] Failed to refresh cart sections', e);
    } finally {
      this.#hideLoading();
    }
  };

  /**
   * 使用 Section Rendering API 获取最新购物车相关片段并更新 DOM
   * @private
   * @returns {Promise<void>}
   */
  #refreshCartSections = async () => {
    if (!window.Shopify || !window.Shopify.routes) return;

    // 在刷新前记录抽屉是否已打开，以便刷新后保持其可见
    const dialogEl = this.querySelector('dialog');
    const wasOpen = dialogEl?.open;

    const sections = (window?.Shopify?.theme?.cartUpdateSections) ?? ['cart-drawer'];
    if (!Array.isArray(sections) || sections.length === 0) return;

    const url = `${window.Shopify.routes.root}?sections=${sections.join(',')}&timestamp=${Date.now()}`;

    const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) throw new Error(`Failed to fetch cart sections: ${res.status}`);

    const htmlSections = await res.json();

    sections.forEach((sectionId) => {
      const html = htmlSections[sectionId];
      if (!html) return;

      if (sectionId === 'cart-drawer') {
        // 更新当前 cart-drawer-component 内部内容，但保留现有 <dialog> 元素的打开状态
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newInner = doc.querySelector('.cart-drawer__inner');
        if (newInner) {
          const currentInner = this.querySelector('.cart-drawer__inner');
          if (currentInner) {
            currentInner.replaceWith(newInner);
          } else {
            // 如果未找到现有 inner，则回退到整体替换
            const newDrawer = doc.querySelector('cart-drawer-component');
            if (newDrawer) {
              this.replaceChildren(...newDrawer.childNodes);
            }
          }
        }
      } else {
        // 其他 section 通过 ID 或 data-section-id 定位
        const container = document.getElementById(sectionId) || document.querySelector(`[data-section-id="${sectionId}"]`);
        if (container) {
          container.innerHTML = html;
        }
      }
    });

    // 触发自定义事件，供其他脚本监听购物车已刷新
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    // 如果抽屉在刷新前就是打开状态，刷新后确保其保持打开
    if (wasOpen) {
      this.showDialog();
    }
  };

  open() {
    // 当用户主动点击购物车图标时，也先刷新一次内容，确保展示最新购物车状态
    (async () => {
      try {
        await this.#refreshCartSections();
      } catch (e) {
        console.warn('[cart-drawer] refresh on open failed', e);
      } finally {
        this.showDialog();
      }
    })();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
